import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import * as lojaBilling from '../platform/billing';
import { caminhoDeCobranca } from '../platform/cobranca';
import { isNative } from '../platform/native';
import { organizarPlanos } from './planosLoja';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCreditCard, FaLock, FaCheckCircle } from 'react-icons/fa';
import api from '../api';
import { AuthContext } from '../context/AuthContext';
import { theme, t } from '../theme';
import { tokenizeCard } from '../services/pagarmeTokenize';

/**
 * Exigido pela App Store (guideline 3.1.2) NO BINARIO, alem dos metadados.
 *
 * No ponto da compra tem que aparecer: titulo da assinatura, duracao, preco por
 * periodo e **links funcionais** para Politica de Privacidade e Termos de Uso.
 * Os tres primeiros ja estavam nos cartoes de plano; os links faltavam, e a
 * submissao de agosto/2026 voltou pela metade equivalente disso nos metadados.
 *
 * Os links apontam para as ROTAS DO PROPRIO APP e nao para uma URL externa. No
 * WebView do Capacitor um link externo ou navega para fora do app ou depende do
 * sistema abrir o navegador — nos dois casos o revisor pode registrar como
 * "link nao funcional". Rota interna abre na hora, sem rede e sem sair do app.
 *
 * O texto de renovacao tambem e cobrado: a Apple quer que o app explique que a
 * assinatura renova sozinha e onde se cancela — nao basta "cancele quando
 * quiser".
 */
const TermosDaAssinatura = ({ naLoja }) => (
  <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${theme.colors.border}` }}>
    <p style={{ fontSize: 11, lineHeight: 1.6, color: theme.colors.textMuted, margin: 0 }}>
      A assinatura renova automaticamente ao fim de cada período e é cobrada na
      {naLoja ? ' sua conta da loja' : ' forma de pagamento cadastrada'}. Para não
      renovar, cancele até 24 horas antes do fim do período
      {naLoja ? ', pelos ajustes da sua conta na loja.' : '.'}
    </p>
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
      <Link to="/termos" style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 600 }}>
        Termos de Uso
      </Link>
      <Link to="/privacidade" style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 600 }}>
        Política de Privacidade
      </Link>
    </div>
  </div>
);

const BANDEIRAS = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', elo: 'Elo',
  hipercard: 'Hipercard', discover: 'Discover', jcb: 'JCB', diners: 'Diners',
};

const formatCard = (v) => v.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim();
const formatExp = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};
const formatDoc = (v, tipo) => {
  const d = v.replace(/\D/g, '');
  if (tipo === 'CPF') return d.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, dd) => dd ? `${a}.${b}.${c}-${dd}` : `${a}.${b}.${c}`).replace(/^\.|\.$|-$/g, (m) => m === '-' ? '' : m);
  return d.slice(0, 14);
};
const formatCep = (v) => v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d{0,3})/, (_, a, b) => b ? `${a}-${b}` : a);
const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{0,2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => [a && `(${a})`, b, c && `-${c}`].filter(Boolean).join(' ').trim());
  return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

export default function Assinatura() {
  const { tenant, refreshTenant } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  // Paywall da loja: so existe no app, e so quando o RevenueCat esta configurado.
  const naLoja = lojaBilling.disponivel();
  // Onde a cobranca PODE acontecer. Ver platform/cobranca.js: dentro do app,
  // sem loja, a resposta e "indisponivel" — nunca o cartao. Cair no pagar.me
  // dentro do binario e o que reprova pela guideline 3.1.1, e foi o que estava
  // acontecendo no iOS por causa de uma chave de ambiente faltando.
  const caminho = caminhoDeCobranca({ nativo: isNative(), lojaDisponivel: naLoja });
  // Ref e nao dependencia: o recarregarStatus e usado em varios efeitos e
  // recria-lo a cada mudanca de tenant provocaria requisicao em cascata.
  const tenantRef = useRef(tenant);
  tenantRef.current = tenant;
  const [pacotes, setPacotes] = useState([]);
  const [comprando, setComprando] = useState(false);

  // Alem de atualizar a tela, propaga para o AuthContext: e o `tenant` que
  // decide a faixa de teste e os bloqueios pelo app inteiro. Sem isso, quem
  // acabou de comprar continuava vendo "periodo de teste" ate sair e entrar.
  const recarregarStatus = useCallback(
    () => api.get('/billing/status')
      .then(r => {
        setStatus(r.data);
        if (refreshTenant && tenantRef.current) {
          refreshTenant({ ...tenantRef.current, ...r.data });
        }
      })
      .catch(() => {}),
    []   // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Precos vem da loja, nunca fixos no codigo: moeda local, promocao e imposto
  // sao decididos por ela.
  useEffect(() => {
    if (!naLoja || !tenant?.slug) return;
    let vivo = true;
    lojaBilling.ofertas(tenant.slug).then(r => {
      if (vivo && r.ok) setPacotes(r.pacotes);
    });
    return () => { vivo = false; };
  }, [naLoja, tenant?.slug]);

  const comprarNaLoja = useCallback(async (pacote) => {
    setError(null); setComprando(true);
    const r = await lojaBilling.comprar(tenant?.slug, pacote);
    setComprando(false);
    if (r.ok || r.motivo === 'ja-assinante') {
      // Quem libera o acesso e o webhook do RevenueCat no backend — aqui so
      // recarregamos o estado. Evita o app achar que liberou sem o servidor saber.
      // recarregarStatus ja propaga para o AuthContext.
      await recarregarStatus();
      toast.success(r.motivo === 'ja-assinante'
        ? 'Sua assinatura já estava ativa — acesso liberado.'
        : 'Assinatura confirmada! Bem-vinda.', { autoClose: 6000 });
      return;
    }
    if (r.motivo === 'cancelado') return;              // desistiu, sem alarde
    setError('Não consegui concluir a compra. Tente de novo em instantes.');
  }, [tenant?.slug]);   // eslint-disable-line react-hooks/exhaustive-deps

  const restaurarCompras = useCallback(async () => {
    setError(null); setComprando(true);
    const r = await lojaBilling.restaurar(tenant?.slug);
    setComprando(false);
    if (!r.ok) { setError('Não consegui verificar suas compras agora.'); return; }
    await recarregarStatus();
    if (!r.ativo) { setError('Nenhuma assinatura ativa encontrada nesta conta da loja.'); return; }
    toast.success('Assinatura restaurada — acesso liberado.', { autoClose: 6000 });
  }, [tenant?.slug]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [docType, setDocType] = useState('CPF');
  const [doc, setDoc] = useState('');
  const [phone, setPhone] = useState('');
  const [cep, setCep] = useState('');
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  useEffect(() => {
    recarregarStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const [mm, yy] = cardExp.split('/');
      const { token, last4, brand } = await tokenizeCard({
        number: cardNumber, holder_name: cardName,
        exp_month: mm, exp_year: yy, cvv: cardCvv,
      });
      const [areaCode, ...rest] = phone.replace(/\D/g, '').match(/^(\d{2})(\d+)$/)?.slice(1) || [];
      const phonePayload = areaCode ? { area_code: areaCode, number: rest[0] } : undefined;

      const res = await api.post('/billing/subscribe', {
        card_token: token, card_last4: last4, card_brand: brand,
        nome: cardName, document: doc, document_type: docType,
        phone: phonePayload,
        address: { line_1: line1, zip_code: cep, city, state },
      });
      if (res.data?.tenant && refreshTenant) refreshTenant(res.data.tenant);
      // Antes daqui saia so um `navigate` mudo: a tela trocava e nada dizia que
      // a cobranca passou. Numa tela de pagamento silencio vira duvida, e a
      // pessoa tenta de novo achando que falhou.
      toast.success('Assinatura confirmada! Pagamento aprovado.', { autoClose: 6000 });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.erro || err.message || 'Erro ao processar pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const statusAtivo = status?.subscription_status === 'active'
    || status?.subscription_status === 'grandfathered';
  const planos = organizarPlanos(pacotes);

  const valor = status?.plan_valor ?? 39.90;
  const valorFmt = valor.toFixed(2).replace('.', ',');

  const statusLabel = {
    active: { text: 'Assinatura ativa', cor: theme.colors.success, bg: theme.colors.successLight },
    past_due: { text: 'Pagamento pendente', cor: theme.colors.warning, bg: theme.colors.warningLight },
    canceled: { text: 'Cancelada', cor: theme.colors.danger, bg: theme.colors.dangerLight },
    grandfathered: { text: 'Acesso liberado', cor: theme.colors.success, bg: theme.colors.successLight },
    pending_payment: null,
  }[status?.subscription_status];

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', background: theme.colors.bgSection, padding: '32px 16px' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 4 }}>🎂</div>
          <h1 style={{ ...t.h1, fontSize: 24, marginBottom: 4 }}>
            {status?.subscription_status === 'active' ? 'Sua assinatura' : 'Ativar assinatura'}
          </h1>
          <p style={{ color: theme.colors.textSecondary, fontSize: 14, margin: 0 }}>
            Desbloqueie o Metria completo para a {tenant?.nome}
          </p>
        </div>

        {/* ── Paywall da loja (app) ────────────────────────────────
            No app a cobranca e da loja, sempre. Mostrar formulario de cartao
            aqui reprova na Apple (3.1.1) e na politica de pagamentos da Play.
            Por isso este ramo substitui o de cartao, nao convive com ele. */}
        {naLoja && (
          <>
            {statusAtivo ? (
              <div style={{ ...t.card, padding: 20, marginBottom: 16 }}>
                <div style={{ textAlign: 'center', color: theme.colors.success, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                  <FaCheckCircle style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Assinatura ativa
                </div>
                <p style={{ fontSize: 13, color: theme.colors.textSecondary, textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                  A cobrança é feita pela {status?.gerenciar_em || 'loja'}. Para trocar de
                  plano, alterar o cartão ou cancelar, use a central de assinaturas dela.
                </p>
              </div>
            ) : (
              <>
                {status?.trial_days_left > 0 && (
                  <div style={{
                    padding: '12px 16px', background: theme.colors.warningLight, color: theme.colors.warning,
                    borderRadius: 10, marginBottom: 16, fontSize: 13, lineHeight: 1.6, textAlign: 'center',
                  }}>
                    Você ainda tem <strong>{status.trial_days_left} {status.trial_days_left === 1 ? 'dia' : 'dias'}</strong> de
                    teste grátis. Ao assinar agora, a cobrança começa hoje e os dias restantes se perdem.
                  </div>
                )}

                {planos.lista.length === 0 ? (
                  <div style={{ ...t.card, padding: 20, marginBottom: 16, textAlign: 'center', fontSize: 14, color: theme.colors.textSecondary }}>
                    Não consegui carregar os planos agora. Verifique a conexão e tente de novo.
                  </div>
                ) : planos.lista.map((pk) => {
                  const anual = pk === planos.anual;
                  return (
                    <button
                      key={pk.id}
                      onClick={() => comprarNaLoja(pk)}
                      disabled={comprando}
                      style={{
                        ...t.card, width: '100%', textAlign: 'left', cursor: comprando ? 'wait' : 'pointer',
                        padding: 18, marginBottom: 12, display: 'block',
                        border: `2px solid ${anual ? theme.colors.primary : theme.colors.border}`,
                        opacity: comprando ? 0.6 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>
                          {anual ? 'Anual' : 'Mensal'}
                        </span>
                        <span style={{ fontSize: 22, fontWeight: 800, color: theme.colors.primary }}>
                          {pk.precoFormatado}
                        </span>
                      </div>
                      {anual && planos.economia && (
                        <div style={{ fontSize: 13, color: theme.colors.success, fontWeight: 600, marginTop: 6 }}>
                          Economize {planos.economia}% em relação ao mensal
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 6 }}>
                        {anual ? 'Cobrado uma vez por ano' : 'Cobrado todo mês'} · cancele quando quiser
                      </div>
                    </button>
                  );
                })}

                {error && <div style={t.error}>{error}</div>}

                {/* Exigido pelas duas lojas: quem troca de aparelho precisa
                    recuperar a assinatura sem pagar de novo. */}
                <button
                  onClick={restaurarCompras}
                  disabled={comprando}
                  style={{
                    background: 'none', border: 'none', width: '100%', padding: 12,
                    color: theme.colors.primary, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Já assinei — restaurar compras
                </button>

                <TermosDaAssinatura naLoja />
              </>
            )}

            <button onClick={() => navigate('/')} style={{ ...t.btnPrimary, width: '100%', padding: 14, marginTop: 8 }}>
              {statusAtivo ? 'Ir para o Metria →' : 'Voltar'}
            </button>
          </>
        )}

        {/* ── Cobranca pela web (pagar.me) ───────────────────────── */}
        {caminho === 'indisponivel' && (
          <div style={{ ...t.card, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, marginBottom: 6 }}>
              Não consegui falar com a loja
            </div>
            <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: '0 0 14px' }}>
              A assinatura deste app é feita pela App Store. Verifique a conexão e
              tente de novo — se continuar, fale com a gente pelo suporte.
            </p>
            <button onClick={() => window.location.reload()} style={{ ...t.btnSecondary, width: '100%', justifyContent: 'center' }}>
              Tentar de novo
            </button>
          </div>
        )}

        {caminho === 'web' && (<>
        {/* Plano */}
        <div style={{ ...t.card, padding: 20, marginBottom: 16, textAlign: 'center', border: `2px solid ${theme.colors.primary}` }}>
          <div style={{ fontSize: 12, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Plano Completo</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: theme.colors.primary }}>R$ {valorFmt}<span style={{ fontSize: 14, color: theme.colors.textSecondary, fontWeight: 500 }}>/mês</span></div>
          <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginTop: 8 }}>
            Fichas ilimitadas · Pedidos · Estoque · Guia de produção
          </div>
        </div>

        {statusLabel && (
          <div style={{ padding: '12px 16px', background: statusLabel.bg, borderRadius: 10, marginBottom: 16, textAlign: 'center', color: statusLabel.cor, fontWeight: 600, fontSize: 14 }}>
            <FaCheckCircle style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {statusLabel.text}
            {status?.card_last4 && ` · Cartão •••• ${status.card_last4}`}
          </div>
        )}

        {status?.subscription_status !== 'active' && status?.subscription_status !== 'grandfathered' && (
          <form onSubmit={handleSubmit} style={{ ...t.card, padding: 20 }}>
            {error && <div style={t.error}>{error}</div>}

            <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginTop: 0, marginBottom: 12 }}>
              <FaCreditCard style={{ marginRight: 6, color: theme.colors.primary }} /> Dados do cartão
            </h3>

            <div style={{ marginBottom: 10 }}>
              <label style={t.label}>Número do cartão</label>
              <input value={cardNumber} onChange={e => setCardNumber(formatCard(e.target.value))} style={t.input} placeholder="0000 0000 0000 0000" inputMode="numeric" required />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={t.label}>Nome no cartão</label>
              <input value={cardName} onChange={e => setCardName(e.target.value.toUpperCase())} style={t.input} placeholder="COMO ESTA NO CARTAO" required />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={t.label}>Validade</label>
                <input value={cardExp} onChange={e => setCardExp(formatExp(e.target.value))} style={t.input} placeholder="MM/AA" inputMode="numeric" required />
              </div>
              <div style={{ flex: 1 }}>
                <label style={t.label}>CVV</label>
                <input value={cardCvv} onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} style={t.input} placeholder="123" inputMode="numeric" required />
              </div>
            </div>

            <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginBottom: 12, paddingTop: 8, borderTop: `1px solid ${theme.colors.border}` }}>
              Dados de cobrança
            </h3>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 100 }}>
                <label style={t.label}>Tipo</label>
                <select value={docType} onChange={e => { setDocType(e.target.value); setDoc(''); }} style={t.select}>
                  <option value="CPF">CPF</option>
                  <option value="CNPJ">CNPJ</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={t.label}>{docType}</label>
                <input value={doc} onChange={e => setDoc(formatDoc(e.target.value, docType))} style={t.input} required />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={t.label}>Celular</label>
              <input value={phone} onChange={e => setPhone(formatPhone(e.target.value))} style={t.input} placeholder="(19) 99999-9999" inputMode="tel" required />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 140 }}>
                <label style={t.label}>CEP</label>
                <input value={cep} onChange={e => setCep(formatCep(e.target.value))} style={t.input} placeholder="00000-000" inputMode="numeric" required />
              </div>
              <div style={{ flex: 1 }}>
                <label style={t.label}>Endereço</label>
                <input value={line1} onChange={e => setLine1(e.target.value)} style={t.input} placeholder="Rua, número" required />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={t.label}>Cidade</label>
                <input value={city} onChange={e => setCity(e.target.value)} style={t.input} required />
              </div>
              <div style={{ width: 100 }}>
                <label style={t.label}>UF</label>
                <input value={state} onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))} style={t.input} maxLength={2} required />
              </div>
            </div>

            <button type="submit" disabled={loading} style={{ ...t.btnPrimary, width: '100%', padding: 14, fontSize: 15, opacity: loading ? 0.6 : 1 }}>
              <FaLock size={12} style={{ marginRight: 6 }} />
              {loading ? 'Processando...' : `Assinar por R$ ${valorFmt}/mês`}
            </button>
            <p style={{ fontSize: 11, color: theme.colors.textMuted, textAlign: 'center', margin: '12px 0 0' }}>
              Pagamento seguro processado pelo pagar.me. Você pode cancelar quando quiser.
            </p>
            <TermosDaAssinatura naLoja={false} />
          </form>
        )}

        {(status?.subscription_status === 'active' || status?.subscription_status === 'grandfathered') && (
          <button onClick={() => navigate('/')} style={{ ...t.btnPrimary, width: '100%', padding: 14, marginTop: 8 }}>
            Ir para o Metria →
          </button>
        )}
        </>)}
      </div>
    </div>
  );
}
